import { Check, Loader2, MessageSquare, Plus } from 'lucide-react';
import { EntityCard } from '@/components/admin/EntityCard';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { capabilityColors } from '@/lib/capability-colors';

export interface PromptOption {
    id?: string;
    name: string;
    description?: string;
}

interface AgentPromptStepProps {
    appKey: string;
    selectedPrompt: PromptOption | null;
    onSelectPrompt: (prompt: PromptOption | null) => void;
    onCreateNew: () => void;
}

export function AgentPromptStep({
    appKey,
    selectedPrompt,
    onSelectPrompt,
    onCreateNew,
}: AgentPromptStepProps) {
    const { data, isLoading } = useCapabilityList(appKey, {
        type: 'prompt',
        per_page: 100,
    });
    const prompts: PromptOption[] = data?.data ?? [];

    return (
        <div className="space-y-4">
            <p className="text-center text-sm text-slate-500">
                Prompt selection is optional. You can skip and configure later.
            </p>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            ) : (
                <div>
                    <div
                        className="grid w-full gap-4"
                        style={{
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(265px, 322px))',
                        }}
                    >
                        {/* Create New Prompt card */}
                        <button
                            type="button"
                            onClick={onCreateNew}
                            className="text-left"
                        >
                            <EntityCard
                                accentColor="border-l-emerald-500"
                                iconBgColor="bg-emerald-100"
                                iconColor="text-emerald-600"
                                className="border-dashed border-2 hover:border-emerald-400"
                            >
                                <EntityCard.Icon>
                                    <Plus size={18} />
                                </EntityCard.Icon>
                                <EntityCard.Title>Create Prompt</EntityCard.Title>
                                <EntityCard.Subtitle>
                                    <span className="text-[10px] text-emerald-600">
                                        Add a new prompt template
                                    </span>
                                </EntityCard.Subtitle>
                                <EntityCard.Description>
                                    Create a reusable prompt template with
                                    variables.
                                </EntityCard.Description>
                            </EntityCard>
                        </button>

                        {/* Existing prompts */}
                        {prompts.map((prompt) => {
                            const isSelected =
                                selectedPrompt?.name === prompt.name;
                            return (
                                <button
                                    key={prompt.id ?? prompt.name}
                                    type="button"
                                    onClick={() =>
                                        onSelectPrompt(
                                            isSelected
                                                ? null
                                                : {
                                                      id: prompt.id,
                                                      name: prompt.name,
                                                      description:
                                                          prompt.description,
                                                  },
                                        )
                                    }
                                    className="text-left"
                                >
                                    <EntityCard
                                        selected={isSelected}
                                        accentColor={
                                            capabilityColors.prompt.border
                                        }
                                        iconBgColor={
                                            capabilityColors.prompt.bgLight
                                        }
                                        iconColor={
                                            capabilityColors.prompt.text
                                        }
                                    >
                                        <EntityCard.Icon>
                                            <MessageSquare size={18} />
                                        </EntityCard.Icon>
                                        <EntityCard.Title>{prompt.name}</EntityCard.Title>
                                        {prompt.description && (
                                            <EntityCard.Description>
                                                {prompt.description}
                                            </EntityCard.Description>
                                        )}
                                        {isSelected && (
                                            <EntityCard.TopRight>
                                                <Check
                                                    size={14}
                                                    className="text-blue-600"
                                                />
                                            </EntityCard.TopRight>
                                        )}
                                    </EntityCard>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {!isLoading && prompts.length === 0 && (
                <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                    <div className="text-center text-slate-500">
                        <MessageSquare
                            size={32}
                            className="mx-auto mb-2 text-slate-300"
                        />
                        <p className="text-sm">No prompts found</p>
                        <p className="mt-1 text-xs">
                            Create a new prompt to get started
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
